import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { LPRLogVM } from '../../../../../models/orderModel';
import { BackOfficeService } from '../../../../../services/backoffice.service';

@Component({
  selector: 'app-lpr-log-details-page',
  templateUrl: './lpr-log-details-page.component.html',
  styleUrl: './lpr-log-details-page.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LprLogDetailsPageComponent implements OnInit, OnDestroy {
  lprLog: LPRLogVM | null = null;
  selectedImageIndex: number = 0;
  isLoading: boolean = false;
  isFullscreen: boolean = false;
  zoomLevel: number = 1;
  isDragging: boolean = false;
  panX: number = 0;
  panY: number = 0;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private backofficeService: BackOfficeService
  ) { }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const organizationId = params['organizationId'];
      const logId = params['logId'];

      if (organizationId && logId) {
        this.isLoading = true;
        this.backofficeService.getLPRLogDetailsById(organizationId, logId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: result => {
              this.lprLog = result;
              this.isLoading = false;
            },
            error: () => {
              this.isLoading = false;
            }
          });
      } else {
        this.goBack();
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/backoffice24', 'lpr-logs']);
  }

  openOrderDetails(orderId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }

  selectImage(index: number): void {
    if (index >= 0 && index < (this.lprLog?.images?.length || 0)) {
      this.selectedImageIndex = index;
      this.resetPan();
    }
  }

  nextImage(): void {
    if (this.lprLog?.images && this.selectedImageIndex < this.lprLog.images.length - 1) {
      this.selectedImageIndex++;
    }
  }

  previousImage(): void {
    if (this.selectedImageIndex > 0) {
      this.selectedImageIndex--;
    }
  }

  parseMessage(message: string): any {
    try {
      return JSON.parse(message);
    } catch {
      return { Text: message };
    }
  }

  getStatus(): string {
    if (!this.lprLog) return 'Unknown';
    return this.lprLog.action || 'Unknown';
  }

  getDirectionLabel(): string {
    if (!this.lprLog) return '-';
    return this.lprLog.direction === 'in' ? 'Entry' : this.lprLog.direction === 'out' ? 'Exit' : this.lprLog.direction;
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    this.zoomLevel = 1;
    this.resetPan();
  }

  zoomIn(): void {
    if (this.zoomLevel < 3) {
      this.zoomLevel += 0.25;
      this.resetPan();
    }
  }

  zoomOut(): void {
    if (this.zoomLevel > 0.5) {
      this.zoomLevel -= 0.25;
      this.resetPan();
    }
  }

  resetZoom(): void {
    this.zoomLevel = 1;
    this.resetPan();
  }

  private resetPan(): void {
    this.panX = 0;
    this.panY = 0;
  }

  private clampPan(): void {
    const container = this.isFullscreen
      ? document.querySelector('.fullscreen-image-container') as HTMLElement
      : document.querySelector('.image-wrapper') as HTMLElement;
    const img = container?.querySelector('img') as HTMLImageElement | null;
    if (!container || !img) {
      return;
    }
    // use natural size so scaling only from zoomLevel
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    const imgW = img.naturalWidth * this.zoomLevel;
    const imgH = img.naturalHeight * this.zoomLevel;
    // maximum translation from center before edge touches container
    const maxX = Math.max(0, (imgW - containerW) / 2);
    const maxY = Math.max(0, (imgH - containerH) / 2);
    this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
    this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
  }

  onDragStart(event: MouseEvent): void {
    if (this.zoomLevel > 1) {
      this.isDragging = true;
      event.preventDefault();
    }
  }

  onDrag(event: MouseEvent): void {
    if (this.isDragging && event.buttons === 1) {
      event.preventDefault();
      // update pan offsets following cursor movement
      this.panX += event.movementX;
      this.panY += event.movementY;
      this.clampPan();
    }
  }

  onDragEnd(): void {
    this.isDragging = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
